import React from "react"
import PropTypes from "prop-types"

/**
 * SEO tags for Gatsby's Head API.
 *
 * Returns a bare fragment of `<title>`/`<meta>` elements.
 *
 * **Must be rendered from a page's `Head` export, not from the page body:**
 *
 *     export const Head = ({ data }) => <Seo title={…} description={…} />
 *
 * Gatsby only hoists the `Head` export into `<head>`; the same JSX in the body would emit
 * meta tags into `<body>`, where they do nothing. `Head` may only be defined in a page or
 * template, never in a shared component — but it can *render* one, which is why this stays a
 * component.
 *
 * Deliberately not set here, because `Head` cannot: the `<html lang>` attribute and the single
 * viewport meta live in `src/html.js`, and the font preloads in `gatsby-ssr.js`. `Head` cannot
 * set attributes on `<html>`, and it has no equivalent for site-global tags.
 */
function Seo({
  description,
  imageOpenGraph,
  imageTwitter,
  imageAlt,
  title,
  twitterHandle,
}) {
  const imageType =
    imageOpenGraph && imageOpenGraph.indexOf(".png") !== -1
      ? "image/png"
      : "image/jpeg"

  return (
    <>
      {title && <title>{title}</title>}

      {/* ***********************************************************
       * Basic HTML
       *********************************************************** */}
      {description && <meta name="description" content={description} />}

      {/* ***********************************************************
       * Open Graph protocol
       *
       * OGP is heavily used by Facebook, LinkedIn, iMessage, etc. and
       * often acts as the primary fallback for platforms that have
       * their own spec.
       *********************************************************** */}
      <meta property="og:type" content="website" />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {imageOpenGraph && <meta property="og:image" content={imageOpenGraph} />}
      {imageOpenGraph && (
        <meta property="og:image:secure_url" content={imageOpenGraph} />
      )}
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      {imageOpenGraph && <meta property="og:image:type" content={imageType} />}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* ***********************************************************
       * Twitter
       *
       * These guys have a bit of a custom spec that falls back to OGP
       * if these fields aren't explicitly set. Setting them however
       * makes for a better Twitter experience, so let's set 'em.
       *********************************************************** */}
      <meta property="twitter:card" content="summary_large_image" />
      {twitterHandle && (
        <meta property="twitter:site" content={twitterHandle} />
      )}
      {title && <meta property="twitter:title" content={title} />}
      {description && (
        <meta property="twitter:description" content={description} />
      )}
      {imageTwitter && <meta property="twitter:image" content={imageTwitter} />}
      {imageAlt && <meta property="twitter:image:alt" content={imageAlt} />}
    </>
  )
}

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  imageOpenGraph: PropTypes.string,
  imageTwitter: PropTypes.string,
  imageAlt: PropTypes.string,
  twitterHandle: PropTypes.string,
}

export { Seo }
